import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { PageHeader, GhostButton } from "../components/Primitives";
import KpiGrid from "../components/KpiGrid";
import DashboardCustomizeModal from "../components/DashboardCustomizeModal";
import OpsHealthBadge from "../components/OpsHealthBadge";
import CoverageTrendChart from "../components/CoverageTrendChart";
import ReliabilityTrendChart from "../components/ReliabilityTrendChart";
import SearchableSelect from "../components/SearchableSelect";
import { Settings, RotateCw, MapPin, X, Lock, Link2, Maximize, Minimize, Play, Pause } from "lucide-react";
import { KPI_CATALOG } from "../lib/dashboardPrefs";
import { usePermission } from "../contexts/AuthContext";
import { useBrowserFullscreen } from "../hooks/useBrowserFullscreen";
import { useMouseIdle } from "../hooks/useMouseIdle";
import "./OpsDashboard.css";

/**
 * OpsDashboard — a metrics-first landing page for ops leads.
 *
 * iter308 — Agency-wide, admin-curated layout:
 *   - Config lives in `agencies.ops_dashboard_config` (not per-user
 *     localStorage) so every ops user sees the SAME view. That view is
 *     the one the admin curated for the whole agency.
 *   - Customize button only renders when the caller has
 *     `ops_dashboard:edit` — defaults grant that to admin + super_admin
 *     only. Ops users get `ops_dashboard:view` (read-only).
 *   - Full-metrics report (OperationalMetricsTab) removed — this page
 *     is now a lean tiles + trend + drilldown surface.
 *
 * Site drilldown scopes the Coverage Trend chart to a single site so
 * ops can do focused post-mortems without leaving the page.
 */

const OPS_DEFAULT_VISIBLE = Object.keys(KPI_CATALOG).filter(
  (k) => KPI_CATALOG[k].section === "Operational",
);

const CADENCE_TO_WINDOW = {
  "7d":  { days: 7,  label: "Last 7 days" },
  "30d": { days: 30, label: "Last 30 days" },
  "90d": { days: 90, label: "Last 90 days" },
};

function computeRange(cadence) {
  const now = new Date();
  const days = CADENCE_TO_WINDOW[cadence]?.days ?? 7;
  const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: now.toISOString() };
}

export default function OpsDashboard() {
  const canEditConfig = usePermission("ops_dashboard:edit");
  const [searchParams, setSearchParams] = useSearchParams();

  // Agency-wide layout state. Seeded from backend on mount so every
  // ops user boots into the admin's curated view.
  const [visible, setVisible] = useState(OPS_DEFAULT_VISIBLE);
  const [sectionOverrides, setSectionOverrides] = useState({});
  const [sectionLabels, setSectionLabels] = useState({});
  const [configMeta, setConfigMeta] = useState({ updated_at: null, updated_by: null });
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  // iter310 — cadence + siteFilter live in the URL so a permalink
  // captures the exact view. Every change here setSearchParams to keep
  // browser back/forward + copy-link in sync.
  const [cadence, setCadence] = useState(() => {
    const q = searchParams.get("cadence");
    return CADENCE_TO_WINDOW[q] ? q : "7d";
  });
  const [siteFilter, setSiteFilter] = useState(() => searchParams.get("site") || "");
  const [autoRefresh, setAutoRefresh] = useState(() => searchParams.get("auto") === "1");
  // Bumping this state variable is a cheap way to trigger the charts
  // (which key their fetches off props) to reload without duplicating
  // their queries here.
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const [sites, setSites] = useState([]);
  const selectedSite = useMemo(
    () => (siteFilter ? sites.find((s) => s.id === siteFilter) : null),
    [siteFilter, sites]
  );

  // Keep URL params in sync with the three shareable state pieces so a
  // Copy Link click captures whatever is currently on screen.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (cadence !== "7d") next.set("cadence", cadence); else next.delete("cadence");
    if (siteFilter) next.set("site", siteFilter); else next.delete("site");
    if (autoRefresh) next.set("auto", "1"); else next.delete("auto");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [cadence, siteFilter, autoRefresh, searchParams, setSearchParams]);

  const range = useMemo(() => computeRange(cadence), [cadence]);
  const rangeLabel = CADENCE_TO_WINDOW[cadence]?.label || "Last 7 days";

  // ── Load agency-wide config on mount ────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/agency/ops-dashboard-config");
        const cfg = r.data || {};
        setVisible(Array.isArray(cfg.visible) && cfg.visible.length ? cfg.visible : OPS_DEFAULT_VISIBLE);
        setSectionOverrides(cfg.section_overrides || {});
        setSectionLabels(cfg.section_labels || {});
        setConfigMeta({ updated_at: cfg.updated_at || null, updated_by: cfg.updated_by || null });
      } catch { /* keep defaults if backend fails / no permission */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/dashboard/stats"); setStats(r.data); } catch { /* keep prior */ }
    try {
      const m = await api.get("/metrics/operational", {
        params: { start: range.start, end: range.end },
      });
      setMetrics(m.data);
    } catch { setMetrics(null); }
    setLoading(false);
    setLastRefreshAt(new Date());
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/sites", { params: { include_temporary: false } });
        const data = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setSites(data.filter((s) => (s.status || "active") !== "ended"));
      } catch { /* leave dropdown empty */ }
    })();
  }, []);

  // ── Auto-refresh (iter310) ─────────────────────────────────────
  // Ops leads mount this on a wall TV; keeping the numbers fresh
  // without a page reload is table stakes. Interval only runs when
  // enabled, is cleaned up on unmount / toggle, and bumps a monotonic
  // tick so chart children (which key their fetches off props) reload
  // in lockstep with the tiles.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      load();
      setRefreshTick((n) => n + 1);
    }, 60_000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  // iter310 — browser fullscreen. The existing kiosk-page hook has
  // gesture + Esc handling covered; we just wire the trigger.
  const fs = useBrowserFullscreen();

  // ── Kiosk screensaver (iter311) ────────────────────────────────
  // Only relevant when the dashboard is mounted on a wall TV — so we
  // gate the idle detector on `autoRefresh`. After 5 minutes without
  // mouse / keyboard input, the CSS class dims the sidebar and the
  // header controls so the KPIs + charts steal the viewer's eye.
  // Any mousemove / keypress restores full opacity immediately.
  const kioskIdle = useMouseIdle({ delayMs: 5 * 60 * 1000, disabled: !autoRefresh });
  useEffect(() => {
    if (kioskIdle) {
      document.body.classList.add("ops-dashboard-kiosk-dimmed");
      return () => document.body.classList.remove("ops-dashboard-kiosk-dimmed");
    }
    document.body.classList.remove("ops-dashboard-kiosk-dimmed");
    return undefined;
  }, [kioskIdle]);

  // iter310 — copy the current URL (already carries cadence + site +
  // auto) so a viewer opens the identical view.
  const copyPermalink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Ops Dashboard link copied — shows this exact view");
    } catch {
      toast.error("Copy failed — clipboard permission denied");
    }
  }, []);

  // ── Persist a customization to the backend. Only reachable when the
  //    Customize button is visible (i.e. canEditConfig). Belt-and-braces:
  //    also check server-side (endpoint gated by ops_dashboard:edit).
  const saveConfig = useCallback(async ({ visible: nextVisible, sectionLabels: nextLabels }) => {
    const body = {
      visible: nextVisible,
      section_overrides: sectionOverrides,
      section_labels: nextLabels || {},
    };
    try {
      const r = await api.put("/agency/ops-dashboard-config", body);
      const cfg = r.data || {};
      setVisible(cfg.visible || nextVisible);
      setSectionOverrides(cfg.section_overrides || sectionOverrides);
      setSectionLabels(cfg.section_labels || nextLabels || {});
      setConfigMeta({ updated_at: cfg.updated_at || null, updated_by: cfg.updated_by || null });
      toast.success("Ops Dashboard layout saved — every ops user in this agency sees this view now.");
    } catch (e) {
      toast.error("Failed to save layout" + (e?.response?.data?.detail ? `: ${e.response.data.detail}` : ""));
    }
  }, [sectionOverrides]);

  // Drag-to-reorder handler on KpiGrid also persists to backend when
  // the caller can edit. For read-only ops users we swallow the call
  // silently so the UI still responds locally but the layout snaps
  // back on the next refresh.
  const reorderTiles = useCallback(async (nextOrder) => {
    if (!canEditConfig) return;
    setVisible(nextOrder);
    try {
      await api.put("/agency/ops-dashboard-config", {
        visible: nextOrder,
        section_overrides: sectionOverrides,
        section_labels: sectionLabels,
      });
    } catch { /* toast is noisy on drag; ignore */ }
  }, [canEditConfig, sectionOverrides, sectionLabels]);

  const setTileSection = useCallback(async (kpiId, section) => {
    if (!canEditConfig) return;
    const next = { ...sectionOverrides, [kpiId]: section };
    setSectionOverrides(next);
    try {
      await api.put("/agency/ops-dashboard-config", {
        visible,
        section_overrides: next,
        section_labels: sectionLabels,
      });
    } catch { /* ignore, same reason as reorder */ }
  }, [canEditConfig, sectionOverrides, sectionLabels, visible]);

  return (
    <div data-testid="ops-dashboard-root">
      <PageHeader
        title="Ops Dashboard"
        subtitle={
          canEditConfig
            ? "Every operational metric in one place. Customize this view — every ops user in this agency sees your layout."
            : "Every operational metric in one place. Layout is curated by your agency admin."
        }
        action={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {lastRefreshAt && (
              <span
                className="text-[10px] text-zinc-500 font-mono hidden sm:inline"
                data-testid="ops-last-refresh"
              >
                fresh · {lastRefreshAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
            <GhostButton
              onClick={() => { load(); setRefreshTick((n) => n + 1); }}
              data-testid="ops-dashboard-refresh"
              title="Refresh now"
            >
              <RotateCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </GhostButton>
            <GhostButton
              onClick={() => setAutoRefresh((v) => !v)}
              data-testid="ops-dashboard-autorefresh-toggle"
              title={autoRefresh ? "Auto-refresh is on (every 60s). Click to pause." : "Auto-refresh every 60s"}
              className={autoRefresh ? "text-[#30D158] border-[#30D158]/40" : undefined}
            >
              {autoRefresh ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
              {autoRefresh ? "Auto · 60s" : "Auto off"}
            </GhostButton>
            <GhostButton
              onClick={copyPermalink}
              data-testid="ops-dashboard-copy-link"
              title="Copy a shareable link — captures cadence, site drilldown, and auto-refresh"
            >
              <Link2 className="size-3.5" />
              Copy Link
            </GhostButton>
            {fs.supported && (
              <GhostButton
                onClick={fs.toggle}
                data-testid="ops-dashboard-fullscreen"
                title={fs.active
                  ? "Exit fullscreen (Esc)"
                  : "Enter true fullscreen — hides the browser URL / tab bar. Ideal for wall-mounted TVs."}
                className={fs.active ? "text-[#0A84FF] border-[#0A84FF]/40" : undefined}
              >
                {fs.active ? <Minimize className="size-3.5" /> : <Maximize className="size-3.5" />}
                {fs.active ? "Exit FS" : "Fullscreen"}
              </GhostButton>
            )}
            {canEditConfig ? (
              <GhostButton
                onClick={() => setCustomizeOpen(true)}
                data-testid="ops-dashboard-customize-open"
              >
                <Settings className="size-3.5" />
                Customize
              </GhostButton>
            ) : (
              <div
                className="inline-flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono"
                title="Only agency admins can change the Ops Dashboard layout."
                data-testid="ops-dashboard-view-only-badge"
              >
                <Lock className="size-3" /> View only
              </div>
            )}
          </div>
        }
      />

      <div className="p-8 space-y-10">
        <OpsHealthBadge cadence={cadence} onCadenceChange={setCadence} />

        <div className="border border-[#27272A] bg-[#0A0A0A] p-4 flex items-center gap-3 flex-wrap" data-testid="ops-site-drilldown">
          <div className="flex items-center gap-2 text-zinc-300">
            <MapPin className="size-4 text-[#0A84FF]" />
            <span className="text-xs uppercase tracking-widest font-bold">Site Drilldown</span>
          </div>
          <SearchableSelect
            testId="ops-site-select"
            triggerClassName="w-full max-w-xs"
            options={[
              { value: "", label: "All sites (agency-wide)" },
              ...sites.map((s) => ({ value: s.id, label: s.name, hint: s.address || undefined })),
            ]}
            value={siteFilter}
            onChange={setSiteFilter}
            placeholder="All sites (agency-wide)"
            emptyHint="No sites match"
            allowClear={!!siteFilter}
          />
          {selectedSite && (
            <button
              type="button"
              onClick={() => setSiteFilter("")}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30]"
              data-testid="ops-site-clear"
            >
              <X className="size-3" /> Clear
            </button>
          )}
          <div className="ml-auto text-[10px] text-zinc-500">
            {selectedSite
              ? <>Chart scoped to <span className="text-zinc-200 font-mono">{selectedSite.name}</span>. Tiles remain agency-wide.</>
              : "Pick a site to drill down."}
          </div>
        </div>

        <section aria-labelledby="ops-kpi-heading" className="space-y-4" data-testid="ops-kpi-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 id="ops-kpi-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
                Key Metrics
              </h2>
              <p className="text-zinc-500 text-sm mt-1">
                Live pulse · <span className="text-zinc-300">{rangeLabel}</span>
                {canEditConfig && " · drag any tile to reorder or move it between sections."}
                {configMeta.updated_by && (
                  <span className="ml-2 font-mono text-[10px] text-zinc-600">
                    · last edited by {configMeta.updated_by}
                  </span>
                )}
              </p>
            </div>
          </div>
          <KpiGrid
            visible={visible}
            stats={stats}
            metrics={metrics}
            role="admin"
            sectionOverrides={sectionOverrides}
            sectionLabels={sectionLabels}
            onReorder={canEditConfig ? reorderTiles : undefined}
            onSectionChange={canEditConfig ? setTileSection : undefined}
          />
        </section>

        <section aria-labelledby="ops-trend-heading" className="space-y-4" data-testid="ops-trend-section">
          <div>
            <h2 id="ops-trend-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
              Trends
            </h2>
            <p className="text-zinc-500 text-sm mt-1">
              Daily coverage (top) and reliability signals — tardies, early clock-outs, no-shows (bottom) — over the selected window.
            </p>
          </div>
          <CoverageTrendChart
            start={range.start}
            end={range.end}
            siteId={siteFilter || null}
            cadenceLabel={rangeLabel}
            refreshTick={refreshTick}
          />
          <ReliabilityTrendChart
            start={range.start}
            end={range.end}
            siteId={siteFilter || null}
            cadenceLabel={rangeLabel}
            refreshTick={refreshTick}
          />
        </section>
      </div>

      {customizeOpen && canEditConfig && (
        <DashboardCustomizeModal
          open={customizeOpen}
          role="admin"
          visible={visible}
          onClose={() => setCustomizeOpen(false)}
          onSave={(next) => { saveConfig(next); setCustomizeOpen(false); }}
          sectionLabels={sectionLabels}
        />
      )}
    </div>
  );
}
